/* eslint-disable react-hooks/exhaustive-deps */
import { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import i18nContext from 'components/i18n-context';
import BottomPartForm from './BottomPartForm';
import { clearJunctionModel } from './Models';
import clearJunctionValidation from './ModelValidators/clearJunctionValidation';
import { GBP_SORT_CODE_REG_EXP, getAccountNumberRegExp } from './ModelValidators/utils';
import { ReactComponent as LocalIcon } from 'assets/payments.svg';
import { ReactComponent as IbanIcon } from 'assets/payment-advanced.svg';
import { CURRENCY_TYPES, PAYMENT_METHOD, RECIPIENT_TYPES } from 'components/common/constants';
import { amountFormattedValue, formatSortCode, prepareFieldErrors, strToFloat } from 'services/utils';
import { IconButton } from 'uikit/IconButton/IconButton';
import Input from 'uikit/Input/Input';
import { InputDropDown } from 'uikit/InputDropDown/InputDropDown';

const LOCAL_TAB = 'LOCAL_ACCOUNT';
const IBAN_TAB = 'IBAN';

const ClearJunctionTable = ({
  customerNumber,
  calculateCommission,
  checkTransfer,
  uploadDocuments,
  setIban,
  setIsNonIban,
  ibanCredentials,
  ibanWarning,
  clearIbanCredentials,
  account,
  isRepeatPayment,
  checkIban,
  checkAccountNumberAndSortCode,
  paymentMethod,
  resetPaymentMethods,
  uploadedFiles,
  commission,
  removePaymentFile,
  error,
  ibanValidationError,
  setError,
  isCommissionLoading,
  isLoading,
  isSuccess,
  currency,
  validationProps,
  isFormResetAfterAccountChanging,
  setIsFormResetAfterAccountChanging,
  setIsPreparedTransactionsCreation,
  isForApprovalTransactionEdit,
  isUserCanCreatePreparedTransaction,
  isUserCanSendTransaction,
  validateForApprovalTransaction,
  paymentPurposes,
  providerType
}) => {
  const i18n = useContext(i18nContext);
  const model = clearJunctionModel;
  const validationSchema = clearJunctionValidation({ ...validationProps, model });
  // Merge general error and IBAN validation error
  const effectiveError = error || ibanValidationError;
  const serverFieldsErrors = prepareFieldErrors(i18n, effectiveError);
  const [disabled, setDisabled] = useState(true);
  const form = useFormik(validationSchema);
  const {
    values,
    errors,
    handleSubmit,
    handleChange,
    validateField,
    validateForm,
    resetForm,
    setFieldValue,
    setErrors
  } = form;
  const [activeTab, setActiveTab] = useState(
    currency === CURRENCY_TYPES.GBP && !values.recipient_wallet ? LOCAL_TAB : IBAN_TAB
  );
  const bankCountry = ibanCredentials?.bank_country || values.bank_country;
  const currentPaymentPurposes =
    paymentPurposes?.find((item) => item.transferProvider === providerType)?.paymentPurposes?.[bankCountry] || null;
  const shouldShowPaymentPurpose = !!currentPaymentPurposes;
  const paymentPurposeOptions = currentPaymentPurposes
    ? Object.entries(currentPaymentPurposes).map(([key, value]) => ({ key, value: `${key} - ${value}` }))
    : [];
  const isPaymentPurposeMissing = shouldShowPaymentPurpose && !values.payment_purpose;

  useEffect(() => {
    if (activeTab === LOCAL_TAB) {
      setIsNonIban(true);
    } else {
      setIsNonIban(false);
    }
  }, [activeTab]);

  useEffect(() => {
    if (isRepeatPayment) {
      const asyncOperations = async () => {
        // Prioritize the IBAN when present — mirrors `activeTab`'s own init logic above
        // (`!values.recipient_wallet ? LOCAL_TAB : IBAN_TAB`). A repeat payment's prefilled data can
        // carry both the original IBAN and its backend-derived account_number/sort_code regardless
        // of which tab the original payment actually used; validating via the wrong pair returns a
        // spurious "Sort Code not found" failure even though the IBAN itself is fine. See TRN-7316.
        // TRN-7283: a repeated GBP payment made via account number + sort code (the LOCAL_TAB pair)
        // never had `recipient_wallet` set, so handleIbanComplete's checkIban call was always a
        // no-op for it and validate-recipient never fired — mirror the IBAN branch for that pair too.
        if (values.recipient_wallet) {
          await handleIbanComplete(false);
        } else if (values.account_number && values.sort_code) {
          await handleAccountNumberAndSortCodeComplete();
        } else {
          await handleIbanComplete(false);
        }
      };

      asyncOperations();
    } else {
      resetForm();
      setIsFormResetAfterAccountChanging(true);
    }
    // `validationProps?.previousTransaction` is a fresh object reference every time "Repeat
    // transaction" is triggered (even for the same transaction and the same sender wallet) — without
    // it, repeating a second time without leaving this page never re-runs the lookup above, since
    // `account.wallet_number` alone doesn't change. See TRN-7316.
  }, [account.wallet_number, validationProps?.previousTransaction]);

  useEffect(() => {
    if (isSuccess) {
      resetForm({});
      setDisabled(true);
    }
  }, [isSuccess]);

  useEffect(() => {
    const asyncOperations = async () => {
      if (!isFormResetAfterAccountChanging) return;

      if (paymentMethod === PAYMENT_METHOD.INTERNAL && activeTab === LOCAL_TAB) {
        setFieldValue('account_number', '');
        setFieldValue('sort_code', '');
        delete errors['account_number'];
        delete errors['sort_code'];
        setErrors(errors);
        setActiveTab(IBAN_TAB);
      }

      if (
        (isRepeatPayment && !values.recipient_wallet && activeTab === LOCAL_TAB) ||
        (!isRepeatPayment && activeTab === IBAN_TAB)
      ) {
        if (values.amount && paymentMethod) {
          await calculateCommission(customerNumber, {
            walletNumber: account?.wallet_number,
            paymentMethod: paymentMethod,
            amount: strToFloat(values.amount?.toString() || ''),
            bankCountry: ibanCredentials?.bank_country || values.bank_country
          });
        }

        if (!isForApprovalTransactionEdit) {
          await handleCheckTransfer(undefined);
        }
      }
    };

    asyncOperations();
  }, [paymentMethod, isRepeatPayment, isFormResetAfterAccountChanging, activeTab]);

  const handleCheckTransfer = async (
    fieldName,
    { skipAccountNumberSortCodeErrorCheck = false, ibanCredentialsOverride } = {}
  ) => {
    if (fieldName) await validateField(fieldName);
    if (
      !serverFieldsErrors?.iban &&
      // `serverFieldsErrors` comes from props computed at this render — a caller that just
      // finished its own fresh account_number/sort_code validation (see
      // handleAccountNumberAndSortCodeComplete) knows the real, current outcome before this
      // component has re-rendered with it, and passes skipAccountNumberSortCodeErrorCheck to
      // avoid trusting this potentially-stale snapshot instead.
      (skipAccountNumberSortCodeErrorCheck || !serverFieldsErrors?.account_number) &&
      (skipAccountNumberSortCodeErrorCheck || !serverFieldsErrors?.sort_code) &&
      // A fieldless validation error (e.g. "Sort Code not found in bank directory") isn't captured
      // by serverFieldsErrors above (it has no field to attach to) — without this check, a failed
      // IBAN/account+sort-code lookup wouldn't stop this check from firing anyway. See TRN-7316.
      !ibanValidationError &&
      (values.recipient_wallet || (values.account_number && values.sort_code)) &&
      // No resolved payment method (e.g. the pair only resolved to methods this wallet doesn't
      // support) means there's nothing valid to send — without this guard the check call goes out
      // with an empty payment_method and comes back as a raw, untranslated backend field error.
      paymentMethod &&
      values.amount &&
      values.recipient_type &&
      values.comment &&
      (values.recipient_type === RECIPIENT_TYPES.COMPANY
        ? values.company_name
        : values.first_name && values.last_name) &&
      (shouldShowPaymentPurpose ? values.payment_purpose : true)
    ) {
      const errorsResult = await validateForm();
      if (!Object.keys(errorsResult).length) {
        await checkTransfer(
          customerNumber,
          model({
            providerTableData: values,
            // A caller that just resolved fresh credentials (handleIbanComplete /
            // handleAccountNumberAndSortCodeComplete) passes them explicitly instead of relying on
            // the `ibanCredentials` prop, which is a snapshot from this render and won't reflect a
            // validation that completed later in the same async chain, before any re-render.
            data: { wallet: account, paymentMethod, ...(ibanCredentialsOverride ?? ibanCredentials) }
          })
        );
        setDisabled(false);
      }
    }
  };

  // Clear payment_purpose when bank_country changes from UAE to another country
  useEffect(() => {
    if (values.bank_country && values.bank_country !== 'AE' && values.payment_purpose) {
      setFieldValue('payment_purpose', '');
    }
  }, [values.bank_country]);

  const handleRecipientTypeChange = (name, data) => handleChange({ target: { name, value: data } });
  const onAmountIBANChange = (e) => {
    handleChange(e);
    if (e.target.name === 'recipient_wallet') {
      setIban(e.target.value);
    }
  };

  const getCommissionValue = () => {
    if (!commission.value && !commission.currency) {
      return '0.00';
    }
    return `${commission.commissionDetails?.displayAmount || amountFormattedValue(commission.value)}`;
  };

  const handleActiveTab = (activeTab) => {
    // Also clear on a fieldless ibanValidationError with no ibanCredentials set (a failed lookup
    // never populates ibanCredentials) — otherwise switching tabs leaves a stale validation error
    // blocking checks on the OTHER tab too. See TRN-7316.
    if (ibanCredentials || ibanValidationError) clearIbanCredentials();
    if (error) setError(null);
    if (!paymentMethod) resetPaymentMethods();
    if (activeTab === LOCAL_TAB) {
      setFieldValue('recipient_wallet', '');
      delete errors['recipient_wallet'];
    }
    if (activeTab === IBAN_TAB) {
      setFieldValue('account_number', '');
      setFieldValue('sort_code', '');
      delete errors['account_number'];
      delete errors['sort_code'];
    }
    setErrors(errors);
    setActiveTab(activeTab);
  };

  const onSortCodeChange = (e) => {
    let inputValue = e.target.value;
    let numericValue = formatSortCode(inputValue);
    setFieldValue('sort_code', numericValue);
  };

  const handleIbanComplete = async (causedOnBlur) => {
    if (causedOnBlur) await validateField('recipient_wallet');
    const checkIbanResult = values.recipient_wallet && (await checkIban(values.recipient_wallet));
    if (causedOnBlur && !isForApprovalTransactionEdit) {
      await handleCheckTransfer(undefined, { ibanCredentialsOverride: checkIbanResult?.ibanCredentials });
    }
  };

  // Mirrors handleIbanComplete above, but for the account_number + sort_code pair — wired to the
  // onBlur of BOTH fields (order of entry shouldn't matter), only actually calling the backend once
  // both are present and already pass client-side format validation, so an obviously-malformed pair
  // doesn't round-trip to the server for nothing.
  const handleAccountNumberAndSortCodeComplete = async (fieldName) => {
    // `fieldName` is omitted when this runs as part of repeat-payment prefill (no single field was
    // just blurred) — mirrors handleIbanComplete's `causedOnBlur` gating above: skip re-validating
    // a field nothing touched, and let the other effect (keyed on isFormResetAfterAccountChanging)
    // own triggering handleCheckTransfer instead of doing it twice.
    if (fieldName) await validateField(fieldName);
    const normalizedSortCode = values.sort_code?.trim();
    const isAccountNumberValid = !!values.account_number && getAccountNumberRegExp().test(values.account_number.trim());
    // `new RegExp(...)` re-wraps the shared `g`-flagged constant into a fresh instance — reusing it
    // directly would carry `lastIndex` state across calls (a global-flag regex's `.test()` alternates
    // true/false on repeated calls against the same object).
    const isSortCodeValid = !!normalizedSortCode && new RegExp(GBP_SORT_CODE_REG_EXP).test(normalizedSortCode);

    // undefined = format was invalid, so the server round trip above never happened — falls
    // through to handleCheckTransfer same as before (its own validateForm() call already blocks
    // on the client-side format error in that case).
    let isPairValid;
    let freshIbanCredentials;
    if (isAccountNumberValid && isSortCodeValid) {
      const checkResult = await checkAccountNumberAndSortCode(values.account_number.trim(), normalizedSortCode);
      isPairValid = checkResult.isValid;
      freshIbanCredentials = checkResult.ibanCredentials;
    }

    // The pair we just checked came back unsupported/invalid — don't let handleCheckTransfer's
    // own (stale, render-scoped) serverFieldsErrors snapshot wave it through anyway; see TRN-7283.
    if (isPairValid === false) return;

    if (fieldName && !isForApprovalTransactionEdit) {
      await handleCheckTransfer(undefined, {
        skipAccountNumberSortCodeErrorCheck: isPairValid === true,
        ibanCredentialsOverride: freshIbanCredentials
      });
    }
  };

  // Editing a "For approval" payment skips the per-field server check (checkTransfer) — still run the client-side
  // (Yup) validation on blur so an emptied/invalid required field is highlighted with its error.
  const handleFieldBlur = (fieldName) =>
    isForApprovalTransactionEdit ? validateField(fieldName) : handleCheckTransfer(fieldName);

  const bottomPartProps = {
    validateField,
    validateForm,
    values,
    errors,
    error,
    ibanValidationError,
    handleChange,
    handleSubmit,
    serverFieldsErrors,
    onAmountIBANChange,
    calculateCommission,
    handleCheckTransfer,
    getCommissionValue,
    currency: account.currency,
    currencyPrecision: account?.currency_precision,
    paymentMethod,
    paymentProvider: account.transfer_provider,
    ibanCredentials,
    isSubmitDisabled:
      (isForApprovalTransactionEdit ? false : disabled) ||
      isLoading ||
      isCommissionLoading ||
      !!Object.keys(errors).length ||
      !!Object.keys(serverFieldsErrors).length ||
      !!error ||
      // A fieldless ibanValidationError (see handleCheckTransfer above) leaves serverFieldsErrors
      // empty, so it needs its own check here too — otherwise Send can stay enabled from a stale
      // `disabled === false` left over from an earlier, unrelated successful check. See TRN-7316.
      !!ibanValidationError ||
      !paymentMethod ||
      isPaymentPurposeMissing,
    isLoading: isLoading || isCommissionLoading,
    uploadedFiles,
    uploadDocuments,
    removePaymentFile,
    customerNumber,
    currentWalletNumber: account?.wallet_number,
    commission,
    setIsPreparedTransactionsCreation,
    isForApprovalTransactionEdit,
    isUserCanCreatePreparedTransaction,
    isUserCanSendTransaction,
    validateForApprovalTransaction,
    model: clearJunctionModel,
    setError,
    shouldShowPaymentPurpose,
    paymentPurposeOptions
  };

  return (
    <>
      {currency === CURRENCY_TYPES.GBP && paymentMethod !== PAYMENT_METHOD.INTERNAL && (
        <div className={'navigation-payment'}>
          <IconButton
            className={'navigation-payment-button'}
            text={i18n.getMessage('transfer.divider.simpleForm')}
            active={activeTab === LOCAL_TAB}
            isDisabled={paymentMethod === PAYMENT_METHOD.INTERNAL}
            onClick={() => handleActiveTab(LOCAL_TAB)}
            Icon={LocalIcon}
          />
          <IconButton
            className={'navigation-payment-button'}
            text={i18n.getMessage('transfer.label.iban')}
            active={activeTab === IBAN_TAB}
            onClick={() => handleActiveTab(IBAN_TAB)}
            Icon={IbanIcon}
          />
        </div>
      )}
      {(activeTab === IBAN_TAB || account.currency === CURRENCY_TYPES.EUR) && (
        <div className={'inputs-wrapper'}>
          <Input
            isRequired={true}
            isDisabled={!!values.account_number || !!values.sort_code}
            autoComplete={'off'}
            label={i18n.getMessage('transfer.form.recipientIban.label')}
            name={'recipient_wallet'}
            value={values.recipient_wallet}
            error={
              (!values.account_number && errors.recipient_wallet) ||
              serverFieldsErrors.iban ||
              serverFieldsErrors.recipientIban
            }
            warning={ibanWarning}
            onChange={onAmountIBANChange}
            onBlur={() => handleIbanComplete(true)}
            subText={
              ibanCredentials?.bank_name &&
              i18n.getMessage('transfer.form.bankName.subText', { bankName: ibanCredentials?.bank_name })
            }
          />
        </div>
      )}
      {currency === CURRENCY_TYPES.GBP && activeTab === LOCAL_TAB && (
        <div className={'inputs-wrapper-evenly account-number-and-sort-code-wrapper'}>
          <Input
            isRequired={true}
            isDisabled={!!values.recipient_wallet}
            autoComplete={'off'}
            name={'account_number'}
            label={i18n.getMessage('transfer.form.accountNumberGBP.label')}
            value={values.account_number}
            error={errors.account_number || serverFieldsErrors.account_number}
            onChange={handleChange}
            onBlur={() => handleAccountNumberAndSortCodeComplete('account_number')}
            subText={
              !serverFieldsErrors.account_number &&
              !serverFieldsErrors.sort_code &&
              ibanCredentials?.bank_name &&
              i18n.getMessage('transfer.form.bankName.subText', { bankName: ibanCredentials?.bank_name })
            }
          />
          <Input
            isRequired={true}
            isDisabled={!!values.recipient_wallet}
            autoComplete={'off'}
            name={'sort_code'}
            label={i18n.getMessage('transfer.form.sortCode.label')}
            value={values.sort_code}
            error={errors.sort_code || serverFieldsErrors.sort_code}
            onChange={onSortCodeChange}
            onBlur={() => handleAccountNumberAndSortCodeComplete('sort_code')}
            max={8}
          />
        </div>
      )}
      <div className={`inputs-wrapper ${values.recipient_type}`}>
        <InputDropDown
          label={i18n.getMessage('transfer.form.recipientType.label')}
          autoComplete={'off'}
          name={'recipient_type'}
          value={values.recipient_type}
          onChange={handleRecipientTypeChange}
          options={Object.values(RECIPIENT_TYPES).map((value) => ({ key: value, value }))}
        />
        {values.recipient_type === RECIPIENT_TYPES.COMPANY && (
          <Input
            isRequired={true}
            autoComplete={'off'}
            label={i18n.getMessage('transfer.form.companyName.label')}
            name={'company_name'}
            value={values.company_name}
            error={errors.company_name || serverFieldsErrors.companyName}
            onChange={handleChange}
            onBlur={() => handleFieldBlur('company_name')}
          />
        )}
        {values.recipient_type === RECIPIENT_TYPES.INDIVIDUAL && (
          <Input
            isRequired={true}
            autoComplete={'off'}
            label={i18n.getMessage('transfer.form.firstName.label')}
            name={'first_name'}
            value={values.first_name}
            error={errors.first_name || serverFieldsErrors.firstName}
            onChange={handleChange}
            onBlur={() => handleFieldBlur('first_name')}
          />
        )}
        {values.recipient_type === RECIPIENT_TYPES.INDIVIDUAL && (
          <Input
            isRequired={true}
            autoComplete={'off'}
            label={i18n.getMessage('transfer.form.lastName.label')}
            name={'last_name'}
            value={values.last_name}
            error={errors.last_name || serverFieldsErrors.lastName}
            onChange={handleChange}
            onBlur={() => handleFieldBlur('last_name')}
          />
        )}
      </div>
      <BottomPartForm {...bottomPartProps} />
    </>
  );
};

ClearJunctionTable.propTypes = {
  customerNumber: PropTypes.string,
  calculateCommission: PropTypes.func.isRequired,
  checkTransfer: PropTypes.func.isRequired,
  uploadDocuments: PropTypes.func.isRequired,
  setIban: PropTypes.func,
  setIsNonIban: PropTypes.func,
  ibanCredentials: PropTypes.object,
  ibanValidationError: PropTypes.any,
  ibanWarning: PropTypes.string,
  clearIbanCredentials: PropTypes.func,
  checkIban: PropTypes.func.isRequired,
  checkAccountNumberAndSortCode: PropTypes.func.isRequired,
  isRepeatPayment: PropTypes.bool,
  account: PropTypes.object,
  paymentMethod: PropTypes.string,
  resetPaymentMethods: PropTypes.func,
  commission: PropTypes.object,
  isCommissionLoading: PropTypes.bool,
  isLoading: PropTypes.bool,
  error: PropTypes.any,
  setError: PropTypes.func,
  uploadedFiles: PropTypes.array,
  removePaymentFile: PropTypes.func,
  isSuccess: PropTypes.bool,
  currency: PropTypes.string,
  validationProps: PropTypes.object.isRequired,
  isFormResetAfterAccountChanging: PropTypes.bool,
  setIsFormResetAfterAccountChanging: PropTypes.func,
  setIsPreparedTransactionsCreation: PropTypes.func,
  isForApprovalTransactionEdit: PropTypes.bool,
  isUserCanCreatePreparedTransaction: PropTypes.bool,
  isUserCanSendTransaction: PropTypes.bool,
  validateForApprovalTransaction: PropTypes.func,
  paymentPurposes: PropTypes.array
};

export default ClearJunctionTable;
